export function initServiceMap(root, opts) {
  if (!root) return null;
  const options = opts || {};
  const initialLang = options.lang === 'jp' ? 'ja' : 'en';
  const initialStation = options.station || '';
  const onNavigate = typeof options.onNavigate === 'function' ? options.onNavigate : function () {};
  const previousDocumentLang = document.documentElement.lang;
  const timers = [];
  let markerInterval = null;
  let active = true;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobileQ = matchMedia('(max-width: 899px)');

  const LEGAL = {
    en: 'Note: We do not provide immigration, visa, or settlement legal advice. These matters are referred to our trusted partner professionals.',
    ja: '注：当社は移民・ビザ・定住に関する法的助言を行っておりません。これらの案件は、信頼できる提携専門家にご紹介いたします。'
  };
  const DOCNOTE = {
    en: 'We translate and prepare documents. For legal advice on your application, we connect you with licensed professionals we work with.',
    ja: '書類の翻訳と準備を行います。申請に関する法的助言が必要な場合は、提携する有資格の専門家におつなぎします。'
  };

  const UI = {
    skip: ['Skip to the map', '地図へ移動'],
    startHere: ['Start here', 'ここから'],
    eyebrow: ['Service map', 'サービス路線図'],
    title: ['Six lines. One way across.', '六つの路線、ひとつの道。'],
    tabMap: ['Map', '路線図'],
    tabLine: ['Line', '路線'],
    tabAZ: ['A–Z index', '索引(五十音順)'],
    azTitle: ['Every service, A to Z', '全サービス 五十音順'],
    azJump: ['Jump to', '移動'],
    lede: ['Tell us where you are. The map draws the services that apply, and shows where one hands off to the next.', 'いまの状況を選ぶと、関係するサービスと、その引き継ぎ先が地図に描かれます。'],
    iam: ['Start here — I am…', 'ここから — わたしは…'],
    keyStation: ['Station', '駅'],
    keyInter: ['Interchange', '乗換駅'],
    keyRoute: ['Your route', 'あなたの経路'],
    keyNote: ['Ringed stations are interchanges — where one service hands off to another. Choose a line name to open its full card.', '太い輪の駅は乗換駅。ひとつのサービスが次へ引き継ぐ場所です。路線名を選ぶと詳細が開きます。'],
    detailEmpty: ['Choose any station to see what it is and where it leads.', '駅を選ぶと、内容と行き先が表示されます。'],
    routeEmpty: ['Pick a starting point above and the map draws your route.', '上から出発点を選ぶと、経路が描かれます。'],
    routeEmptyM: ['Pick a starting point above. The stations on your route are marked in the line cards below.', '上から出発点を選ぶと、経路上の駅が下の路線カードに表示されます。'],
    leads: ['Where it leads', '行き先'],
    ixWith: ['Interchange · change here for', '乗換駅 · 乗り換え'],
    yourRoute: ['Your route', 'あなたの経路'],
    clear: ['Clear route', '経路をクリア'],
    viewMap: ['View on map ↑', '地図で見る ↑'],
    line: ['Line', '路線'],
    intertwines: ['Intertwines with', '接続路線'],
    linesTitle: ['The lines', '路線一覧'],
    linesLede: ['The same stations, listed by line. Starred stations carry a note.', '同じ駅を路線ごとに。＊印の駅には注記があります。'],
    shelfTitle: ['The shelf', '商品棚'],
    shelfLede: ['Every service on the other lines has a boxed, priced version here.', 'ほかの路線のサービスは、ここで価格の決まったパッケージになります。'],
    close: ['Close', '閉じる'],
    azLede: ['Looking for one service by name?', 'サービス名から探しますか？'],
    azLink: ['Open the A–Z index →', '五十音順の索引を開く →'],
    mapAria: ['Service map. Six lines; each station is a button.', 'サービス路線図。六つの路線。各駅はボタンです。'],
    openLine: ['Open line card', '路線カードを開く'],
    seeAll: ['See the full line card', '路線カードをすべて見る'],
    langBtn: ['日本語', 'English'],
    langAria: ['Switch to Japanese', '英語に切り替え'],
    stationContact: ['Enquire about this station', 'この駅について相談する'],
    stationProduct: ['View the matching guide', '対応するガイドを見る'],
    lineViewTitle: ['Follow the line', '路線をたどる'],
    lineViewLede: ['One line at a time, end to end. Interchanges jump across to the same station on the other line.', '一路線ずつ、終点まで。乗換駅からは同じ駅の別路線へ移れます。'],
    changeFor: ['Change for', '乗り換え'],
    zoomIn: ['Zoom in', '拡大'],
    zoomOut: ['Zoom out', '縮小']
  };

  const LINES = {
    C: { n: '01', color: '#22406E', name: ['Consulting', 'コンサルティング'], short: ['Consulting', 'コンサル'], tag: ['Know the way before you commit.', '決める前に、道筋を知る。'],
      groups: [
        { t: ['Business', 'ビジネス'], ids: ['c_wh', 'c_bring', 'c_moveb', 'c_open', 'c_ask', 'c_rep', 'c_market', 'c_trips'] },
        { t: ['Academics', 'アカデミック'], ids: ['c_school', 'c_exch', 'c_summer', 'c_transfer', 'c_explore', 'c_movesch', 'c_univ'] },
        { t: ['Travel', 'トラベル'], ids: ['c_visit', 'c_tours', 'c_temp', 'c_itin', 'c_family'] }
      ],
      inter: ['A', 'T', 'F'], note: ['Onboarding hands you to the Agent line. Document translation crosses to Translation. A whole business entry can be handed to Full Support.', 'オンボーディングはエージェント路線へ。書類翻訳は翻訳路線へ。事業進出をまるごと任せる場合はフルサポートへ引き継ぎます。'] },
    A: { n: '02', color: '#8E2118', name: ['Personal Agent', 'パーソナルエージェント'], short: ['Agent', 'エージェント'], tag: ['A specialist by your side.', '専任が、そばに。'],
      groups: [
        { t: ['By your side', 'そばで'], ids: ['a_ded', 'a_onb', 'a_err', 'a_onsite', 'a_calls', 'a_house', 'a_family', 'a_check', 'a_emerg', 'a_leave', 'a_ref'] },
        { t: ['Across', '横断'], across: [['Academics', 'アカデミック', 'c_school'], ['Travel', 'トラベル', 'c_visit'], ['Business', 'ビジネス', 'c_ask'], ['Translation', '翻訳', 't_doc'], ['Onboarding', 'オンボーディング', 'a_onb']] }
      ],
      inter: ['C', 'T', 'P', 'S', 'F'], note: ['The only line that touches every other one. That is the point of it.', 'ほかのすべての路線に接する唯一の路線。それがこの路線の意味です。'] },
    T: { n: '03', color: '#1D5E55', name: ['Translation & Interpretation', '翻訳・通訳'], short: ['Translation', '翻訳・通訳'], tag: ['Every word, understood.', 'すべての言葉を、確かに。'],
      groups: [
        { t: ['Domains', '分野'], ids: ['t_med', 't_visa', 't_travel', 't_law', 't_every', 't_local', 't_biz', 't_acad'] },
        { t: ['Formats', '形式'], ids: ['t_doc', 't_live', 't_type', 't_vol', 't_proof', 't_remote'] }
      ],
      inter: ['A', 'P', 'C'], note: ['Interpreters ride the Agent line. Volume work becomes a Product.', '通訳者はエージェント路線に乗ります。まとまった分量の翻訳はプロダクトになります。'] },
    P: { n: '04', color: '#7A5A12', name: ['Products', 'プロダクト'], short: ['Products', 'プロダクト'], tag: ['Clear scope. Clear price.', '範囲も価格も、明快に。'],
      groups: [ { t: ['The shelf', '商品棚'], ids: ['p_guide', 'p_tail', 'p_less', 'p_class', 'p_cons', 'p_slot', 'p_trans', 'p_rates', 'p_hours', 'p_seminar', 'p_tours', 'p_full'] } ],
      inter: ['C', 'T', 'S', 'A', 'F'], note: ['Every service on the other lines has a boxed, priced version here.', 'ほかの路線のサービスは、ここで価格の決まったパッケージになります。'] },
    S: { n: '05', color: '#5E3B72', name: ['Lessons & Classes', 'レッスン・クラス'], short: ['Lessons & Classes', 'レッスン'], tag: ['Learn it for good.', '身につく学びを。'],
      groups: [ { t: ['Formats', '形式'], ids: ['s_sem', 's_less', 's_class', 's_one', 's_solo', 's_culture', 's_biz', 's_exam', 's_kids', 's_online'] } ],
      inter: ['P', 'A'], note: ['Seats and lesson plans are sold on the Products line.', '受講枠とレッスンプランはプロダクト路線で販売しています。'] },
    F: { n: '06', color: '#4E6B2A', name: ['Full Support', 'フルサポート'], short: ['Full Support', 'フルサポート'], tag: ['We carry the whole crossing.', '渡航を、まるごと引き受けます。'],
      groups: [
        { t: ['End to end', 'まるごと'], ids: ['f_reloc', 'f_biz', 'f_study', 'f_leave'] },
        { t: ['Run by', '担当'], ids: ['f_coord'] }
      ],
      inter: ['A', 'C', 'P'], note: ['Full Support doesn’t replace the other lines — your coordinator books them for you. It hands off to your Personal Agent day to day, to Consulting for the business plan, and to Products for one priced package.', 'フルサポートはほかの路線の代わりではありません。担当コーディネーターが各路線を手配し、日々のことはパーソナルエージェントへ、事業計画はコンサルティングへ、料金はプロダクトのパッケージへ引き継ぎます。'] }
  };
  const ORDER = ['C', 'A', 'T', 'P', 'S', 'F'];

  const S = {};
  const def = (id, line, en, ja, de, dj, leads, o = {}) => { S[id] = { id, line, name: [en, ja], d: [de, dj], leads, ...o }; };
  def('c_wh', 'C', 'Working holiday', 'ワーキングホリデー', 'Plan the year before you go: timing, preparation, arrival and the first months, put in the right order.', '出発前に一年を設計します。時期、準備、到着、最初の数か月を正しい順番に。', ['a_onb', 'p_guide', 's_less']);
  def('c_bring', 'C', 'Bringing your business', '事業を持ち込む', 'Taking an existing business across: what changes, what carries over, and what to check first.', '既存の事業を海の向こうへ。変わること、引き継げること、最初に確認すべきこと。', ['c_rep', 't_local', 'a_ded']);
  def('c_moveb', 'C', 'Moving for business', '仕事での移住', 'Relocating for work or a posting, planned around the job and the household.', '仕事や赴任での移住を、仕事と家族の両方に合わせて計画します。', ['a_onb', 't_doc', 'a_err']);
  def('c_open', 'C', 'Opening a business', '起業する', 'Starting fresh on the other side: structure, local setup and the first partners.', '現地での新規立ち上げ。体制、現地の準備、最初のパートナー。', ['t_law', 'p_cons', 'a_ref']);
  def('c_ask', 'C', 'Asking about business', 'ビジネスの相談', 'One focused conversation when you have a question and want a straight answer.', '質問があり、率直な答えが欲しいときの一回の相談。', ['p_cons', 'c_open']);
  def('c_rep', 'C', 'Representing your company', '企業の代理', 'A local presence for your company: meetings, liaison and follow-through.', '会社の現地窓口として、会議・連絡・フォローを担います。', ['t_live', 'a_ded']);
  def('c_market', 'C', 'Market & entry research', '市場・参入調査', 'Before you commit: the market, the competition and what entry would take.', '決める前に、市場、競合、参入に必要なことを調べます。', ['c_open', 'c_bring', 'p_cons']);
  def('c_trips', 'C', 'Business trips & site visits', '出張・現地視察', 'Short trips to meet partners, see sites and test the ground.', 'パートナーとの面会や現地視察のための短期出張。', ['t_live', 'c_rep', 'a_err']);
  def('c_school', 'C', 'Schooling & enrolment', '学校選びと入学', 'Choosing schools and getting enrolled, from the first shortlist to a confirmed place.', '候補の絞り込みから入学確定まで、学校選びと手続きを。', ['c_explore', 't_doc', 'a_onb']);
  def('c_exch', 'C', 'Exchange student', '交換留学', 'Preparing an exchange term so the time abroad counts.', '留学期間を実りあるものにするための準備。', ['s_less', 'a_onb']);
  def('c_summer', 'C', 'Summer school & credits', 'サマースクールと単位', 'Short programmes, and how their credits carry back home.', '短期プログラムと、その単位の持ち帰り方。', ['c_school', 's_class']);
  def('c_transfer', 'C', 'Transferring schools', '転校', 'Moving between schools or school systems without losing ground.', '学校や制度の間を、遅れることなく移ります。', ['t_doc', 'c_school']);
  def('c_explore', 'C', 'School exploration', '学校見学', 'Visiting and comparing schools before you decide.', '決める前に学校を訪ね、比べます。', ['c_school', 'a_err']);
  def('c_movesch', 'C', 'Moving for school', '就学のための移住', 'Relocating a student or a family around the school year.', '学年に合わせた、学生や家族の移住計画。', ['a_onb', 't_every']);
  def('c_univ', 'C', 'University applications', '大学出願', 'Choosing universities and preparing applications, from shortlist to offer.', '大学選びから出願準備、合格まで。', ['t_acad', 'c_school']);
  def('c_visit', 'C', 'Visiting', '訪問', 'A short visit, planned with local knowledge.', '現地の知見で計画する短期訪問。', ['c_tours', 'p_guide']);
  def('c_tours', 'C', 'Tours', 'ツアー', 'Guided cultural and business tours.', '文化・ビジネスのガイド付きツアー。', ['t_travel', 'a_onsite', 'p_tours']);
  def('c_temp', 'C', 'Temporary stays', '短期滞在', 'Weeks or months, set up so daily life works from the first day.', '数週間から数か月。初日から日常が回るように整えます。', ['a_onb', 't_every']);
  def('c_itin', 'C', 'Itinerary planning', '旅程づくり', 'Routes, timing and bookings planned around what you came to do.', '目的に合わせて、ルート・日程・予約を組み立てます。', ['p_tours', 't_travel']);
  def('c_family', 'C', 'Family trips', '家族旅行', 'Trips planned for children, parents and everyone in between.', '子どもから親世代まで、家族みんなの旅を計画します。', ['c_itin', 'p_guide']);

  def('a_ded', 'A', 'Dedicated specialist', '専任スペシャリスト', 'One named person who knows your situation and stays with it.', '状況を理解した一人の担当者が、最後まで伴走します。', ['a_onb', 'a_err', 'a_ref'], { map: ['Dedicated|specialist', '専任|スペシャリスト'] });
  def('a_onb', 'A', 'Onboarding & settling in', 'オンボーディングと生活の立ち上げ', 'The first weeks: housing, accounts, phone, transit and local routines.', '最初の数週間。住まい、口座、携帯、交通、暮らしの手順。', ['a_err', 't_every', 'p_tail'], { also: 'C', map: ['Onboarding &|settling in', 'オンボーディング|生活の立ち上げ'] });
  def('a_err', 'A', 'Accompanied errands & appointments', '同行サポート', 'Someone beside you at the bank, the clinic, the school or the office counter.', '銀行、病院、学校、窓口へ同行します。', ['a_onsite', 't_med'], { map: ['Accompanied errands|& appointments', '同行サポート'] });
  def('a_onsite', 'A', 'On-site language support', '現地での言語サポート', 'Language help in the moment, wherever the conversation happens.', '会話が起きるその場で、言語をサポートします。', ['t_live', 's_less'], { map: ['On-site language|support', '現地での|言語サポート'] });
  def('a_calls', 'A', 'Calls made for you', '電話の代行', 'We make the call for you, or join it — with the clinic, the landlord, the school or the office.', '病院、大家、学校、役所などへの電話を代わりにかける、または同席します。', ['t_remote', 'p_hours'], { map: ['Calls made|for you', '電話の代行'] });
  def('a_house', 'A', 'Housing search', '住まい探し', 'Finding a place to live, viewing it with you and getting the lease signed.', '物件探し、内見の同行、賃貸契約まで。', ['t_every', 'a_onb']);
  def('a_family', 'A', 'Family & school-run support', '家族・送迎サポート', 'Help for the household: school runs, family appointments and the routines around them.', '学校の送迎、家族の用事、その周りの日常を支えます。', ['c_school', 'a_err', 's_kids'], { map: ['Family &|school-run support', '家族・|送迎サポート'] });
  def('a_check', 'A', 'Regular check-ins', '定期フォロー', 'Scheduled check-ins so small problems are caught early.', '小さな問題を早めに見つけるための定期的な連絡。', ['a_ded'], { map: ['Regular|check-ins', '定期|フォロー'] });
  def('a_emerg', 'A', 'Emergency / after-hours contact', '緊急・時間外連絡', 'A number to call when something goes wrong outside office hours.', '時間外に何かあったときに連絡できる窓口。', ['t_med', 'a_calls'], { map: ['Emergency /|after-hours contact', '緊急・|時間外連絡'] });
  def('a_leave', 'A', 'Leaving & moving out', '退去・帰国準備', 'Closing the lease, accounts and contracts when it’s time to go.', '帰るときの賃貸解約、口座、契約の整理。', ['f_leave', 't_every'], { map: ['Leaving &|moving out', '退去・|帰国準備'] });
  def('a_ref', 'A', 'Referral to licensed professionals', '有資格専門家のご紹介', 'When a matter needs a licensed professional, we know who to send you to — and we make the introduction.', '有資格の専門家が必要な案件では、適切な相手を知っています。ご紹介までを行います。', ['t_visa', 't_doc'], { flag: true, map: ['Referral to licensed|professionals', '有資格専門家の|ご紹介'] });

  def('t_med', 'T', 'Medical', '医療', 'Translation and interpretation for appointments, records and care.', '診察、記録、ケアのための翻訳・通訳。', ['t_live', 'a_err']);
  def('t_visa', 'T', 'Visa & immigration documents', 'ビザ・移民申請書類', 'Certified translation of documents required for visa and immigration applications.', 'ビザ・移民申請に必要な書類の認証翻訳。', ['t_doc', 'a_ref'], { flag: true });
  def('t_travel', 'T', 'Travel', '旅行', 'Bookings, signage, menus and the conversations on the way.', '予約、案内表示、メニュー、道中の会話。', ['c_visit', 'a_onsite']);
  def('t_law', 'T', 'Law', '法務', 'Translation of contracts and legal documents.', '契約書や法的文書の翻訳。', ['t_doc', 'a_ref']);
  def('t_every', 'T', 'Everyday services', '日常の手続き', 'Leases, utilities, school notices and the paperwork of daily life.', '賃貸契約、公共料金、学校のお知らせなど、暮らしの書類。', ['a_err', 't_doc']);
  def('t_local', 'T', 'Localization', 'ローカライズ', 'Websites, products and marketing adapted for the other market.', 'ウェブサイト、製品、販促物を相手市場向けに。', ['c_bring', 't_vol']);
  def('t_biz', 'T', 'Business & commercial', 'ビジネス・商業', 'Proposals, contracts, correspondence and commercial documents.', '提案書、契約書、取引文書などの翻訳。', ['c_bring', 't_doc']);
  def('t_acad', 'T', 'Academic records (transcripts, diplomas)', '学業書類(成績証明書・卒業証書)', 'Translation of transcripts, diplomas and certificates for schools and employers.', '学校や勤務先に提出する成績証明書・卒業証書などの翻訳。', ['c_univ', 't_doc'], { mapUp: ['Academic records', '学業書類'] });
  def('t_doc', 'T', 'Document translation', '文書翻訳', 'Written translation, certified where required.', '必要に応じて認証付きの書面翻訳。', ['t_type', 'p_trans']);
  def('t_live', 'T', 'Live interpretation', '通訳', 'An interpreter in the room or on the call.', '会議室でも通話でも、通訳が同席します。', ['a_onsite', 't_vol'], { also: 'A', map: ['Live|interpretation', '通訳'] });
  def('t_type', 'T', 'By type & level', '種類・レベル別', 'Priced by document type and level of certification.', '文書の種類と認証レベルで価格を設定。', ['p_trans']);
  def('t_vol', 'T', 'By volume or duration', '分量・時間別', 'Priced by word count, or by the hour for interpretation.', '文字数、または通訳の時間で価格を設定。', ['p_trans', 'p_rates']);
  def('t_proof', 'T', 'Proofreading & review', '校正・チェック', 'A second pair of eyes on a translation you already have.', 'お手元の翻訳を、もう一度確認します。', ['t_doc', 'p_rates'], { map: ['Proofreading|& review', '校正・|チェック'] });
  def('t_remote', 'T', 'Remote interpretation (video / phone)', 'リモート通訳(ビデオ・電話)', 'An interpreter on a video or phone call, wherever you are.', 'どこにいても、ビデオ通話や電話に通訳が入ります。', ['a_calls', 'p_hours'], { map: ['Remote|interpretation', 'リモート|通訳'] });

  def('p_guide', 'P', 'Guide packs', 'ガイドパック', 'Ready-made guides for the most common crossings.', 'よくある渡航に向けた既製ガイド。', ['p_tail', 'c_ask'], { unit: ['per pack · ready-made', '1パック・既製'], rel: ['P'] });
  def('p_tail', 'P', 'Tailored guide packs', 'カスタムガイドパック', 'A guide pack written for your situation by your agent.', '担当エージェントが状況に合わせて作るガイドパック。', ['a_ded'], { also: 'A', unit: ['per pack · built by your agent', '1パック・担当者が作成'], rel: ['P', 'A'], map: ['Tailored|guide packs', 'カスタム|ガイドパック'] });
  def('p_less', 'P', 'Lessons — 1:1 & group', 'レッスン(個人・グループ)', 'Lessons bought singly or in blocks.', '単発またはまとめて購入できるレッスン。', ['s_less'], { unit: ['per lesson · solo or group', '1回・個人またはグループ'], rel: ['P', 'S'], map: ['Lessons —|1:1 & group', 'レッスン|個人・グループ'] });
  def('p_class', 'P', 'Class lessons', 'クラスレッスン', 'A seat in a scheduled course.', '日程の決まったコースの受講枠。', ['s_class'], { unit: ['per course · scheduled', '1コース・日程制'], rel: ['P', 'S'] });
  def('p_cons', 'P', 'Consulting — fixed timeframe', '期間固定コンサルティング', 'Consulting with a fixed scope and a fixed window.', '範囲と期間を固定したコンサルティング。', ['c_ask'], { unit: ['fixed scope · fixed window', '範囲固定・期間固定'], rel: ['P', 'C'], map: ['Consulting —|fixed timeframe', '期間固定|コンサル'] });
  def('p_slot', 'P', 'Consulting slot', 'コンサルティング枠', 'A set number of consulting hours, booked when you want them.', '決まった時間数のコンサルティングを、好きなときに予約できます。', ['c_ask', 'p_cons'], { unit: ['a set number of hours', '時間数を指定'], rel: ['P', 'C'], map: ['Consulting|slot', 'コンサル枠'] });
  def('p_trans', 'P', 'Translation — by category, type & volume', '翻訳(分野・種類・分量別)', 'Translation and interpretation priced up front by category, type and volume or duration.', '分野・種類・分量または時間で、事前に価格が決まる翻訳・通訳。', ['t_doc', 't_live'], { unit: ['by category, type & volume / duration', '分野・種類・分量/時間別'], rel: ['P', 'T'], map: ['Translation —|by category,|type & volume', '翻訳|分野・種類・分量'] });
  def('p_rates', 'P', 'Translation rates — per word or per minute', '翻訳料金(文字単価・分単価)', 'Written work priced per word, interpretation per minute. The rate depends on difficulty: general, legal or medical.', '書面は文字単価、通訳は分単価。料金は難易度(一般・法務・医療)で決まります。', ['t_doc', 't_live', 't_med'], { unit: ['per word / per minute · by difficulty', '文字単価/分単価・難易度別'], rel: ['P', 'T'], map: ['Per word /|per minute', '文字単価|分単価'] });
  def('p_hours', 'P', 'Support hours', 'サポート時間', 'Our time, bought in blocks and used for anything: business interpretation, document translation of any kind, making or joining calls for you.', '当社の時間をまとめて購入し、何にでも使えます。ビジネス通訳、あらゆる書類の翻訳、電話の代行や同席など。', ['a_calls', 't_live', 't_doc'], { unit: ['per block of hours · any task', '時間単位・用途自由'], rel: ['P', 'A', 'T'], map: ['Support|hours', 'サポート|時間'] });
  def('p_seminar', 'P', 'Seminar tickets', 'セミナーチケット', 'A seat at a scheduled seminar.', '日程の決まったセミナーの参加枠。', ['s_sem'], { unit: ['per seat', '1席'], rel: ['P', 'S'], map: ['Seminar|tickets', 'セミナー|チケット'] });
  def('p_tours', 'P', 'Fixed-price tours', '定額ツアー', 'Tours with the route and the price set in advance.', 'ルートと料金が事前に決まったツアー。', ['c_tours'], { unit: ['per tour · price set in advance', '1ツアー・料金確定'], rel: ['P', 'C'], map: ['Fixed-price|tours', '定額|ツアー'] });
  def('p_full', 'P', 'Full Support package', 'フルサポートパッケージ', 'A whole journey, scoped and priced up front as one package.', 'ひとつの渡航全体を、範囲と料金を決めたパッケージで。', ['f_coord'], { also: 'F', unit: ['one scope · one price', '範囲ひとつ・料金ひとつ'], rel: ['P', 'F'], map: ['Full Support|package', 'フルサポート|パッケージ'] });

  def('s_sem', 'S', 'Seminars', 'セミナー', 'Talks on culture, business practice and daily life.', '文化、商習慣、暮らしについてのセミナー。', ['s_class', 'p_seminar']);
  def('s_less', 'S', 'Lessons', 'レッスン', 'One-to-one or small-group lessons in Japanese or English.', '日本語・英語の個人または少人数レッスン。', ['p_less', 'a_onsite'], { also: 'A' });
  def('s_class', 'S', 'Classes', 'クラス', 'Structured courses that run over several weeks.', '数週間にわたる体系的なコース。', ['p_class', 's_one']);
  def('s_one', 'S', 'One-off or ongoing', '単発・継続', 'Take a single session, or keep going.', '一回だけでも、続けても。', ['p_less'], { map: ['One-off or|ongoing', '単発・継続'] });
  def('s_solo', 'S', 'Solo or group', '個人・グループ', 'Learn on your own or alongside others.', '一人でも、仲間とでも。', ['s_less', 's_class']);
  def('s_culture', 'S', 'Culture & etiquette', '文化・マナー', 'How things are done: manners, customs and the unwritten rules.', '暮らしの作法、習慣、言葉にならない決まりごと。', ['s_sem']);
  def('s_biz', 'S', 'Business Japanese / English', 'ビジネス日本語・英語', 'Language for meetings, email and the workplace.', '会議、メール、職場で使う言葉。', ['p_less', 'c_rep'], { map: ['Business|Japanese / English', 'ビジネス|日本語・英語'] });
  def('s_exam', 'S', 'Exam preparation (JLPT, EIKEN)', '試験対策(JLPT・英検)', 'Structured preparation for the JLPT and EIKEN.', 'JLPT・英検に向けた体系的な対策。', ['s_class', 'p_class'], { map: ['Exam prep|JLPT · EIKEN', '試験対策|JLPT・英検'] });
  def('s_kids', 'S', 'Children & family classes', '子ども・家族クラス', 'Classes for children, and for parents learning alongside them.', '子ども向け、そして一緒に学ぶ保護者向けのクラス。', ['s_class', 'a_family'], { map: ['Children &|family classes', '子ども・|家族クラス'] });
  def('s_online', 'S', 'Online or in person', 'オンライン・対面', 'Every lesson and class, online or in the room.', 'すべてのレッスンとクラスを、オンラインでも対面でも。', ['s_less'], { map: ['Online or|in person', 'オンライン|・対面'] });

  def('f_reloc', 'F', 'Relocation, end to end', '移住をまるごと', 'From the first call to settled in: we plan it, book every service it needs and run it for you.', '最初のご相談から生活が落ち着くまで。計画し、必要なサービスをすべて手配して、まとめて進めます。', ['a_house', 'a_onb', 'c_moveb'], { flag: true, map: ['Relocation', '移住'] });
  def('f_biz', 'F', 'Business entry, end to end', '事業進出をまるごと', 'From the first call to operating: research, setup, people on the ground and the paperwork, run as one project.', '最初のご相談から事業開始まで。調査、立ち上げ、現地の人員、書類を、ひとつのプロジェクトとして進めます。', ['c_market', 'c_open', 't_biz'], { also: 'C', flag: true, map: ['Business|entry', '事業進出'] });
  def('f_study', 'F', 'Study move, end to end', '留学・就学をまるごと', 'For the student and the family: from enrolment to settled in.', '学生とご家族のために。入学から生活が落ち着くまで。', ['c_school', 'a_family', 't_acad'], { flag: true, map: ['Study move', '留学・就学'] });
  def('f_leave', 'F', 'Leaving Japan, end to end', '帰国・転出をまるごと', 'The same care on the way out: notices, contracts, the move and the handover.', '帰るときも同じように。届出、契約、引っ越し、引き継ぎまで。', ['a_leave', 't_every'], { flag: true, map: ['Leaving|Japan', '帰国・|転出'] });
  def('f_coord', 'F', 'One coordinator for every line', '全路線をひとりで調整', 'One person who books and runs the other lines for you, and hands off to your Personal Agent day to day.', 'ほかの路線の手配と進行をひとりが担い、日々のことはパーソナルエージェントへ引き継ぎます。', ['a_ded', 'p_full'], { also: 'A', map: ['One coordinator', '専任コーディネーター'] });

  // Kana readings — Japanese A–Z sorts by these, never by the romanised spelling
  const KANA = { c_wh: 'わーきんぐほりでー', c_bring: 'じぎょうをもちこむ', c_moveb: 'しごとでのいじゅう', c_open: 'きぎょうする', c_ask: 'びじねすのそうだん', c_rep: 'きぎょうのだいり', c_market: 'しじょう・さんにゅうちょうさ', c_trips: 'しゅっちょう・げんちしさつ', c_school: 'がっこうえらびとにゅうがく', c_exch: 'こうかんりゅうがく', c_summer: 'さまーすくーるとたんい', c_transfer: 'てんこう', c_explore: 'がっこうけんがく', c_movesch: 'しゅうがくのためのいじゅう', c_univ: 'だいがくしゅつがん', c_visit: 'ほうもん', c_tours: 'つあー', c_temp: 'たんきたいざい', c_itin: 'りょていづくり', c_family: 'かぞくりょこう',
    a_ded: 'せんにんすぺしゃりすと', a_onb: 'おんぼーでぃんぐとせいかつのたちあげ', a_err: 'どうこうさぽーと', a_onsite: 'げんちでのげんごさぽーと', a_calls: 'でんわのだいこう', a_house: 'すまいさがし', a_family: 'かぞく・そうげいさぽーと', a_check: 'ていきふぉろー', a_emerg: 'きんきゅう・じかんがいれんらく', a_leave: 'たいきょ・きこくじゅんび', a_ref: 'ゆうしかくせんもんかのごしょうかい',
    t_med: 'いりょう', t_visa: 'びざ・いみんしんせいしょるい', t_travel: 'りょこう', t_law: 'ほうむ', t_every: 'にちじょうのてつづき', t_local: 'ろーからいず', t_biz: 'びじねす・しょうぎょう', t_acad: 'がくぎょうしょるい', t_doc: 'ぶんしょほんやく', t_live: 'つうやく', t_type: 'しゅるい・れべるべつ', t_vol: 'ぶんりょう・じかんべつ', t_proof: 'こうせい・ちぇっく', t_remote: 'りもーとつうやく',
    p_guide: 'がいどぱっく', p_tail: 'かすたむがいどぱっく', p_less: 'れっすん(こじん・ぐるーぷ)', p_class: 'くらすれっすん', p_cons: 'きかんこていこんさるてぃんぐ', p_slot: 'こんさるてぃんぐわく', p_trans: 'ほんやく(ぶんや・しゅるい・ぶんりょうべつ)', p_rates: 'ほんやくりょうきん', p_hours: 'さぽーとじかん', p_seminar: 'せみなーちけっと', p_tours: 'ていがくつあー', p_full: 'ふるさぽーとぱっけーじ',
    s_sem: 'せみなー', s_less: 'れっすん', s_class: 'くらす', s_one: 'たんぱつ・けいぞく', s_solo: 'こじん・ぐるーぷ', s_culture: 'ぶんか・まなー', s_biz: 'びじねすにほんご・えいご', s_exam: 'しけんたいさく', s_kids: 'こども・かぞくくらす', s_online: 'おんらいん・たいめん',
    f_reloc: 'いじゅうをまるごと', f_biz: 'じぎょうしんしゅつをまるごと', f_study: 'りゅうがく・しゅうがくをまるごと', f_leave: 'きこく・てんしゅつをまるごと', f_coord: 'ぜんろせんをひとりでちょうせい' };
  Object.keys(S).forEach((id) => { S[id].k = KANA[id]; if (!KANA[id]) console.warn('No kana reading for', id); });

  const ENTRY = [
    { k: 'wh', t: ['On a working holiday', 'ワーキングホリデー中'], r: ['c_wh', 'p_guide', 'a_house', 'a_onb', 't_every', 's_less'] },
    { k: 'biz', t: ['Opening or moving a business', '事業の立ち上げ・移転'], r: ['c_market', 'c_ask', 'c_open', 'c_moveb', 'c_rep', 't_law', 'a_ded', 'p_cons'] },
    { k: 'study', t: ['Studying here', 'ここで学ぶ'], r: ['c_explore', 'c_univ', 'c_school', 'c_movesch', 'a_onb', 'a_family', 's_class'] },
    { k: 'visit', t: ['Just visiting', '短期の訪問'], r: ['c_itin', 'c_visit', 'c_tours', 'c_temp', 't_travel', 'p_tours', 'p_guide'] },
    { k: 'med', t: ['Needing medical help', '医療のサポートが必要'], r: ['t_med', 't_live', 'a_calls', 'a_onsite', 'a_err', 'a_emerg'] },
    { k: 'docs', t: ['Sorting out documents', '書類を整えたい'], r: ['t_visa', 't_acad', 't_doc', 't_type', 'a_ref'], note: true },
    { k: 'lang', t: ['Learning the language', '言語を学びたい'], r: ['s_less', 's_class', 's_sem', 's_exam', 'p_class', 'p_less'] }
  ];

  // Map geometry (viewBox 1510 × 910)
  const Y = { C: 220, T: 400, P: 570, S: 730 };
  const A0 = { x: 70, y: 860 }, ATOP = { x: 1145, y: 60 }, FX = 1400;
  const slope = (A0.y - ATOP.y) / (ATOP.x - A0.x);
  const ax = (y) => A0.x + (A0.y - y) / slope;
  const POS = {};
  const step = (x0, d, n) => Array.from({ length: n }, (_, i) => x0 + i * d);
  const alt = (n, first) => Array.from({ length: n }, (_, i) => ((i % 2 === 0) === (first === 'below') ? 'below' : 'above'));
  const put = (ids, xs, y, modes) => ids.forEach((id, i) => { POS[id] = { x: xs[i], y, mode: Array.isArray(modes) ? modes[i] : modes }; });
  put(LINES.C.groups[0].ids, step(90, 50, 8), Y.C, 'up');
  put(LINES.C.groups[1].ids, step(510, 50, 7), Y.C, 'up');
  put(LINES.C.groups[2].ids, [1090, 1150, 1210, 1275, 1340], Y.C, alt(5, 'below'));
  put(['t_med', 't_visa', 't_travel', 't_law', 't_every', 't_local', 't_biz', 't_acad', 't_doc'], step(90, 60, 9), Y.T, 'up');
  put(['t_type', 't_vol', 't_proof', 't_remote'], [920, 1030, 1140, 1250], Y.T, alt(4, 'below'));
  put(['p_guide', 'p_less', 'p_class', 'p_cons', 'p_slot', 'p_trans', 'p_rates', 'p_hours', 'p_seminar', 'p_tours'], [140, 300, 640, 730, 820, 910, 1000, 1090, 1180, 1270], Y.P, ['below', 'above', 'above', 'below', 'above', 'below', 'above', 'below', 'above', 'below']);
  put(['s_sem', 's_class', 's_one', 's_solo', 's_culture', 's_biz', 's_exam', 's_kids', 's_online'], step(360, 110, 9), Y.S, alt(9, 'below'));
  POS.a_onb = { x: ax(Y.C), y: Y.C, mode: 'belowR' };
  POS.t_live = { x: ax(Y.T), y: Y.T, mode: 'belowR' };
  POS.p_tail = { x: ax(Y.P), y: Y.P, mode: 'belowR' };
  POS.s_less = { x: ax(Y.S), y: Y.S, mode: 'belowR' };
  [['a_ded', 860, 'right'], ['a_check', 795, 'left'], ['a_err', 675, 'left'], ['a_family', 620, 'left'], ['a_onsite', 515, 'left'], ['a_calls', 460, 'left'],
   ['a_house', 350, 'right'], ['a_emerg', 300, 'right'], ['a_ref', 150, 'right'], ['a_leave', 105, 'right']].forEach(([id, y, mode]) => { POS[id] = { x: ax(y), y, mode }; });
  POS.f_coord = { x: ATOP.x, y: ATOP.y, mode: 'above' };
  POS.f_reloc = { x: FX, y: 140, mode: 'right' };
  POS.f_biz = { x: FX, y: Y.C, mode: 'right' };
  POS.f_study = { x: FX, y: 300, mode: 'right' };
  POS.f_leave = { x: FX, y: 360, mode: 'right' };
  POS.p_full = { x: FX, y: Y.P, mode: 'aboveL' };
  Object.keys(S).forEach((id) => { if (!POS[id]) console.warn('No map position for', id); });
  const A_SEQ = ['a_ded', 'a_check', 's_less', 'a_err', 'a_family', 'p_tail', 'a_onsite', 'a_calls', 't_live', 'a_house', 'a_emerg', 'a_onb', 'a_ref', 'a_leave', 'f_coord'];
  const F_SEQ = ['f_coord', 'f_reloc', 'f_biz', 'f_study', 'f_leave', 'p_full'];

  const state = { lang: initialLang, entry: null, sel: null, cardSel: {}, view: options.view === 'az' ? 'az' : 'map', mview: 'map', mline: 'C', lvSel: null };
  const L = () => (state.lang === 'ja' ? 1 : 0);
  const tx = (pair) => pair[L()];
  const u = (k) => UI[k][L()];
  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => [...root.querySelectorAll(sel)];
  const isMobile = () => mobileQ.matches;
  const badge = (lid) => `<span class="rbm-badge" style="--lc:${LINES[lid].color}" aria-hidden="true">${lid}</span>`;
  const legalHTML = () => state.lang === 'ja'
    ? `<p class="rbm-legal">${esc(LEGAL.ja)}<span lang="en">${esc(LEGAL.en)}</span></p>`
    : `<p class="rbm-legal">${esc(LEGAL.en)}</p>`;
  const docHTML = () => state.lang === 'ja'
    ? `<p class="rbm-docnote">${esc(DOCNOTE.ja)}</p><p class="rbm-docnote" lang="en" style="font-size:15px;color:#5A544E;margin-top:4px">${esc(DOCNOTE.en)}</p>`
    : `<p class="rbm-docnote">${esc(DOCNOTE.en)}</p>`;
  const hoverText = (st) => {
    const ln = LINES[st.line];
    let s = `${st.line} · ${tx(ln.name)} — ${tx(st.name)}`;
    if (st.also) s += ` · ${u('ixWith')} ${st.also}`;
    if (st.flag) s += `\n${LEGAL.en}${state.lang === 'ja' ? '\n' + LEGAL.ja : ''}`;
    return s;
  };
  const scrollToEl = (el, extra = 12) => {
    if (!el) return;
    const head = $('.rbm-header');
    const top = el.getBoundingClientRect().top + window.scrollY - (head ? head.offsetHeight : 0) - extra;
    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  function renderStatic() {
    root.dataset.lang = state.lang;
    document.documentElement.lang = state.lang;
    $$('[data-i18n]').forEach((el) => { el.textContent = u(el.dataset.i18n); });
    const lb = $('[data-lang-toggle]');
    lb.textContent = u('langBtn');
    lb.setAttribute('aria-label', u('langAria'));
    lb.lang = state.lang === 'en' ? 'ja' : 'en';
    const zin = $('[data-zoom="in"]'), zout = $('[data-zoom="out"]');
    if (zin) zin.setAttribute('aria-label', u('zoomIn'));
    if (zout) zout.setAttribute('aria-label', u('zoomOut'));
    $('[data-map]').setAttribute('aria-label', u('mapAria'));
    $('[data-legend]').setAttribute('aria-label', u('linesTitle'));
  }

  function renderNav() {
    $('[data-nav-root]').innerHTML = ORDER.map((lid) => {
      const ln = LINES[lid];
      const groups = ln.groups.map((g) => {
        const items = g.ids ? g.ids.map((id) => `<li><button type="button" data-go="${id}" title="${esc(hoverText(S[id]))}">${esc(tx(S[id].name))}</button></li>`).join('')
          : g.across.map((a) => `<li><button type="button" data-go="${a[2]}">${esc(state.lang === 'ja' ? a[1] : a[0])}</button></li>`).join('');
        return `<div><h4>${esc(tx(g.t))}</h4><ul>${items}</ul></div>`;
      }).join('');
      const flagged = ln.groups.some((g) => g.ids && g.ids.some((id) => S[id].flag));
      return `<div class="rbm-navitem" style="--lc:${ln.color}">
        <button type="button" class="rbm-navbtn" data-nav="${lid}" aria-expanded="false" aria-controls="rbm-np-${lid}" aria-label="${lid} · ${esc(tx(ln.name))}">${badge(lid)}<span class="rbm-nm-long">${esc(tx(ln.short))}</span></button>
        <div class="rbm-navpanel" id="rbm-np-${lid}" hidden>
          <div class="rbm-navpanel-grid">${groups}</div>
          <div class="rbm-navpanel-foot">${flagged ? legalHTML() : ''}<button type="button" class="rbm-linkbtn" data-line-open="${lid}">${esc(u('seeAll'))} →</button></div>
        </div></div>`;
    }).join('');
  }

  function renderEntry() {
    $('[data-entry]').innerHTML = ENTRY.map((e) => `<button type="button" class="rbm-entry-chip" data-entry-k="${e.k}" aria-pressed="${state.entry === e.k}">${esc(tx(e.t))}</button>`).join('');
    $('[data-legend]').innerHTML = ORDER.map((lid) => `<button type="button" class="rbm-linebtn" style="--lc:${LINES[lid].color}" data-line-open="${lid}" aria-label="${lid} · ${esc(tx(LINES[lid].name))} — ${esc(u('openLine'))}">${badge(lid)}<span>${esc(tx(LINES[lid].name))}</span></button>`).join('');
  }

  function wrapLabel(st) {
    if (st.map) return tx(st.map).split('|');
    const s = tx(st.name);
    if (state.lang === 'ja') return s.length > 8 ? [s.slice(0, 8), s.slice(8)] : [s];
    if (s.length <= 14) return [s];
    const words = s.split(' '); const out = []; let cur = '';
    words.forEach((w) => { if ((cur + ' ' + w).trim().length > 14 && cur) { out.push(cur); cur = w; } else cur = (cur + ' ' + w).trim(); });
    if (cur) out.push(cur);
    return out;
  }

  function labelSVG(st, p) {
    const lines = wrapLabel(st);
    const one = esc(st.mapUp ? tx(st.mapUp) : tx(st.name));
    const tspans = (x, y0, dy) => lines.map((l, i) => '<tspan x="' + x + '" y="' + (y0 + i * dy) + '">' + esc(l) + '</tspan>').join('');
    const n = lines.length;
    switch (p.mode) {
      case 'up': { const x = p.x + 7, y = p.y - 13; return '<text class="lbl" x="' + x + '" y="' + y + '" transform="rotate(-38 ' + x + ' ' + y + ')">' + one + '</text>'; }
      case 'above': return '<text class="lbl" text-anchor="middle">' + tspans(p.x, p.y - 20 - (n - 1) * 15, 15) + '</text>';
      case 'below': return '<text class="lbl" text-anchor="middle">' + tspans(p.x, p.y + 30, 15) + '</text>';
      case 'belowR': return '<text class="lbl">' + tspans(p.x + 14, p.y + 32, 15) + '</text>';
      case 'right': return '<text class="lbl">' + tspans(p.x + 18, p.y + 5 - (n - 1) * 7, 15) + '</text>';
      case 'left': return '<text class="lbl" text-anchor="end">' + tspans(p.x - 16, p.y + 5 - (n - 1) * 7, 15) + '</text>';
      case 'aboveL': return '<text class="lbl" text-anchor="end">' + tspans(p.x - 16, p.y - 20 - (n - 1) * 15, 15) + '</text>';
    }
    return '';
  }

  function renderMap() {
    const svg = $('[data-map]');
    const focusedId = document.activeElement && document.activeElement.closest && document.activeElement.closest('[data-st]') ? document.activeElement.closest('[data-st]').dataset.st : null;
    const parts = [];
    [['Business', 'ビジネス', 84, 446], ['Academics', 'アカデミック', 504, 816], ['Travel', 'トラベル', 1060, 1370]].forEach((g) => {
      const label = state.lang === 'ja' ? g[1] : g[0].toUpperCase();
      parts.push('<text class="rbm-grouphead" x="' + (g[2] + g[3]) / 2 + '" y="14" text-anchor="middle" aria-hidden="true">' + esc(label) + '</text><line class="rbm-grouprule" x1="' + g[2] + '" x2="' + g[3] + '" y1="24" y2="24" aria-hidden="true"/>');
    });
    const ROWEND = { C: FX, T: 1330, P: FX, S: 1300 };
    const aStart = { x: 34, y: A0.y + (A0.x - 34) * slope };
    const LP = {
      C: 'M34 ' + Y.C + 'H' + ROWEND.C, T: 'M34 ' + Y.T + 'H' + ROWEND.T,
      P: 'M34 ' + Y.P + 'H' + ROWEND.P, S: 'M34 ' + Y.S + 'H' + ROWEND.S,
      F: 'M' + ATOP.x + ' ' + ATOP.y + 'H' + (FX - 30) + 'Q' + FX + ' ' + ATOP.y + ' ' + FX + ' ' + (ATOP.y + 30) + 'V622',
      A: 'M' + aStart.x + ' ' + aStart.y + 'L' + ATOP.x + ' ' + ATOP.y
    };
    /* fat invisible hit copies so a fingertip can find a line on phones; taps open the mobile LINE view */
    ORDER.forEach((lid) => parts.push('<path class="rbm-linehit" d="' + LP[lid] + '" data-mline="' + lid + '" aria-hidden="true"/>'));
    ['C', 'T', 'P', 'S'].forEach((lid) => parts.push('<path class="rbm-line" d="' + LP[lid] + '" data-mline="' + lid + '" stroke="' + LINES[lid].color + '" stroke-width="8" aria-hidden="true"/>'));
    parts.push('<path class="rbm-line" d="' + LP.F + '" data-mline="F" stroke="' + LINES.F.color + '" stroke-width="8" aria-hidden="true"/>');
    parts.push('<path class="rbm-line" data-apath d="' + LP.A + '" data-mline="A" stroke="' + LINES.A.color + '" stroke-width="8" aria-hidden="true"/>');
    parts.push('<g data-routeg aria-hidden="true"></g>');
    if (!reduceMotion) parts.push('<circle data-marker r="4.5" cx="-20" cy="-20" fill="' + LINES.A.color + '" stroke="#FCFAF5" stroke-width="2" aria-hidden="true" style="pointer-events:none"/>');
    const roundel = (lid, x, y) => '<g class="rbm-lineroundel" data-line-open="' + lid + '" tabindex="0" role="button" aria-label="' + lid + ' · ' + esc(tx(LINES[lid].name)) + ' — ' + esc(u('openLine')) + '"><title>' + lid + ' · ' + esc(tx(LINES[lid].name)) + '</title><circle class="foc" cx="' + x + '" cy="' + y + '" r="20"/><circle cx="' + x + '" cy="' + y + '" r="15" fill="' + LINES[lid].color + '" stroke="#FCFAF5" stroke-width="3"/><text x="' + x + '" y="' + (y + 5) + '" text-anchor="middle">' + lid + '</text></g>';
    const seq = [];
    ['C', 'T', 'P', 'S'].forEach((lid) => {
      seq.push({ roundel: lid });
      Object.keys(POS).filter((id) => POS[id].y === Y[lid] && S[id]).sort((a, b) => POS[a].x - POS[b].x).forEach((id) => seq.push({ id }));
    });
    seq.push({ roundel: 'A' }); A_SEQ.forEach((id) => seq.push({ id }));
    seq.push({ roundel: 'F' }); F_SEQ.forEach((id) => seq.push({ id }));
    Object.keys(S).forEach((id) => seq.push({ id }));
    const seen = new Set();
    seq.forEach((it) => {
      if (it.roundel) {
        const lid = it.roundel;
        parts.push(lid === 'A' ? roundel('A', aStart.x, aStart.y) : lid === 'F' ? roundel('F', FX, 640) : roundel(lid, 34, Y[lid]));
        return;
      }
      if (seen.has(it.id) || !POS[it.id]) return; seen.add(it.id);
      const st = S[it.id]; const p = POS[it.id]; const ix = !!st.also;
      const color = LINES[st.line].color;
      const r = ix ? 11 : 6.5;
      const lines = ix ? st.line + ' + ' + st.also : st.line;
      const aria = lines + ' · ' + tx(LINES[st.line].name) + ' — ' + tx(st.name) + (ix ? ' (' + u('keyInter') + ')' : '');
      parts.push('<g class="rbm-st' + (ix ? ' is-ix' : '') + '" data-st="' + st.id + '" style="--lc:' + color + '" tabindex="0" role="button" aria-label="' + esc(aria) + '">' +
        '<title>' + esc(hoverText(st)) + '</title>' +
        '<circle class="hit" cx="' + p.x + '" cy="' + p.y + '" r="17"/>' +
        '<circle class="sel" cx="' + p.x + '" cy="' + p.y + '" r="' + (r + 6) + '"/>' +
        '<circle class="foc" cx="' + p.x + '" cy="' + p.y + '" r="' + (r + 10) + '"/>' +
        '<circle class="dot" cx="' + p.x + '" cy="' + p.y + '" r="' + r + '"/>' +
        (ix ? '<circle class="core" cx="' + p.x + '" cy="' + p.y + '" r="3.2"/>' : '') +
        labelSVG(st, p) + '</g>');
    });
    svg.innerHTML = parts.join('');
    updateMap(false);
    if (focusedId) { const el = svg.querySelector('[data-st="' + focusedId + '"]'); if (el) el.focus(); }
  }

  function curve(pts) {
    if (pts.length < 2) return '';
    let d = `M${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
      const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
  }

  const routeIds = () => { const e = ENTRY.find((x) => x.k === state.entry); return e ? e.r : []; };

  function updateMap(animate) {
    const svg = $('[data-map]');
    const ids = routeIds();
    svg.classList.toggle('has-route', ids.length > 0);
    svg.querySelectorAll('[data-st]').forEach((g) => {
      g.classList.toggle('is-route', ids.includes(g.dataset.st));
      g.classList.toggle('is-sel', state.sel === g.dataset.st);
      g.setAttribute('aria-pressed', String(state.sel === g.dataset.st));
    });
    const rg = svg.querySelector('[data-routeg]');
    marker.t0 = Date.now() + (animate && ids.length ? 1300 : 0);
    if (!ids.length) { rg.innerHTML = ''; return; }
    const d = curve(ids.map((id) => POS[id]));
    rg.innerHTML = `<path class="rbm-routecase" d="${d}"/><path class="rbm-routepath" d="${d}"/>`;
    if (animate && !reduceMotion) {
      const paths = rg.querySelectorAll('path');
      const len = paths[1].getTotalLength();
      paths.forEach((p) => { p.style.strokeDasharray = `${len}`; p.style.strokeDashoffset = `${len}`; p.getBoundingClientRect(); p.style.transition = 'stroke-dashoffset 1.3s cubic-bezier(.4,.1,.2,1)'; p.style.strokeDashoffset = '0'; });
    }
  }

  function leadsHTML(st) {
    const chips = st.leads.map((id) => `<button type="button" class="rbm-leadchip" data-go="${id}" title="${esc(hoverText(S[id]))}">${badge(S[id].line)}${esc(tx(S[id].name))}</button>`).join('');
    return `<div class="rbm-leads"><p class="rbm-mono">${esc(u('leads'))}</p><div class="rbm-leadchips">${chips}</div></div>`;
  }
  function actionHTML(st) {
    const product = st.id === 'c_wh' ? '/store#product-working_holiday_guide' : '';
    const href = product || '/contact?station=' + encodeURIComponent(st.name[0]);
    const label = product ? u('stationProduct') : u('stationContact');
    return '<p class="rbm-station-action"><a href="' + href + '" data-station-action="' + st.id + '">' + esc(label) + ' →</a></p>';
  }
  function stationBody(st, level) {
    const ln = LINES[st.line];
    const ix = st.also ? `<p class="rbm-ixnote">${esc(u('ixWith'))} ${badge(st.also)} ${esc(tx(LINES[st.also].name))}</p>` : '';
    const H = level || 'h3';
    return `<div class="rbm-dline" style="--lc:${ln.color}">${badge(st.line)}<span class="rbm-mono">${esc(u('line'))} ${st.line} · ${esc(tx(ln.name))}</span></div>
      <${H}>${esc(tx(st.name))}</${H}>
      <p class="rbm-desc">${esc(tx(st.d))}</p>${ix}${leadsHTML(st)}${actionHTML(st)}${st.flag ? legalHTML() : ''}`;
  }

  function renderPanels() {
    const det = $('[data-detail]');
    det.innerHTML = state.sel ? stationBody(S[state.sel]) : `<p class="rbm-empty">${esc(u('detailEmpty'))}</p>`;
    const rt = $('[data-route]');
    const e = ENTRY.find((x) => x.k === state.entry);
    if (!e) { rt.innerHTML = `<p class="rbm-empty">${esc(u(isMobile() ? 'routeEmptyM' : 'routeEmpty'))}</p>`; return; }
    const flagged = e.r.some((id) => S[id].flag);
    rt.innerHTML = `<div class="rbm-routehead"><p class="rbm-mono">${esc(u('yourRoute'))}</p><button type="button" class="rbm-linkbtn" data-clear>${esc(u('clear'))}</button></div>
      <h3>${esc(tx(e.t))}</h3>
      ${e.note ? docHTML() : ''}
      <ol>${e.r.map((id) => `<li><button type="button" data-go="${id}" title="${esc(hoverText(S[id]))}">${badge(S[id].line)}<span>${esc(tx(S[id].name))}</span></button></li>`).join('')}</ol>
      ${flagged ? legalHTML() : ''}`;
  }

  function cardHTML(lid, ctx) {
    const ln = LINES[lid];
    const groups = ln.groups.map((g) => {
      const chips = g.ids
        ? g.ids.map((id) => { const st = S[id]; return `<button type="button" class="rbm-chip${st.also ? ' is-ix' : ''}" data-chip="${id}"${st.flag ? ' data-flag' : ''} title="${esc(hoverText(st))}">${esc(tx(st.name))}</button>`; }).join('')
        : g.across.map((a) => `<button type="button" class="rbm-chip" data-chip="${a[2]}" title="${esc(hoverText(S[a[2]]))}">${badge(S[a[2]].line)}${esc(state.lang === 'ja' ? a[1] : a[0])}</button>`).join('');
      return `<div class="rbm-cgroup"><p class="rbm-mono">${esc(tx(g.t))}</p><div class="rbm-chips">${chips}</div></div>`;
    }).join('');
    const flagged = ln.groups.some((g) => g.ids && g.ids.some((id) => S[id].flag));
    const inter = ln.inter.map((o) => `<button type="button" class="rbm-interbtn" style="--lc:${LINES[o].color}" data-line-open="${o}">${badge(o)}${esc(tx(LINES[o].name))}</button>`).join('');
    const hid = `rbm-ct-${ctx}-${lid}`;
    return `<article class="rbm-card" data-card="${lid}" data-ctx="${ctx}" ${ctx === 'list' ? `id="rbm-card-${lid}"` : ''} style="--lc:${ln.color}" aria-labelledby="${hid}">
      <div>
        <p class="rbm-mono rbm-cardnum">${badge(lid)}<span>${esc(u('line'))} ${lid} — ${ln.n}</span></p>
        <h3 id="${hid}">${esc(tx(ln.name))}</h3>
        <p class="rbm-tag">${esc(tx(ln.tag))}</p>
        ${ctx === 'list' ? `<button type="button" class="rbm-viewmap" data-viewmap="${lid}">${esc(u('viewMap'))}</button>` : ''}
      </div>
      <div>${groups}<div class="rbm-carddetail" aria-live="polite" data-carddetail></div></div>
      <aside class="rbm-inter"><p class="rbm-mono">${esc(u('intertwines'))}</p><div class="rbm-interbadges">${inter}</div><p class="rbm-internote">${esc(tx(ln.note))}</p>${flagged ? legalHTML() : ''}</aside>
    </article>`;
  }

  function renderCards() {
    $('[data-cards]').innerHTML = ORDER.map((lid) => cardHTML(lid, 'list')).join('');
    updateCards();
  }
  function updateCards() {
    const ids = routeIds();
    $$('.rbm-card').forEach((card) => {
      const lid = card.dataset.card;
      const cur = card.dataset.ctx === 'list' ? state.cardSel[lid] : null;
      card.querySelectorAll('[data-chip]').forEach((c) => {
        const inGroup = !c.querySelector('.rbm-badge');
        c.classList.toggle('is-route', inGroup && ids.includes(c.dataset.chip));
        c.classList.toggle('is-sel', c.dataset.chip === cur);
        c.setAttribute('aria-pressed', String(c.dataset.chip === cur));
      });
      const det = card.querySelector('[data-carddetail]');
      if (det) det.innerHTML = cur ? stationBody(S[cur], 'h4') : '';
    });
  }

  function renderShelf() {
    $('[data-shelf]').innerHTML = LINES.P.groups[0].ids.map((id, i) => {
      const st = S[id];
      return `<button type="button" class="rbm-shelfcard" data-go="${id}" title="${esc(hoverText(st))}">
        <span class="rbm-shelftop"><span>P${i + 1}</span><span>${st.rel.map((r) => badge(r)).join('')}</span></span>
        <b>${esc(tx(st.name))}</b><i>${esc(tx(st.unit))}</i></button>`;
    }).join('');
  }

  const toHira = (s) => s.replace(/[\u30a1-\u30f6]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60));
  const KROWS = [['あ', 'あいうえおぁぃぅぇぉ'], ['か', 'かきくけこ'], ['さ', 'さしすせそ'], ['た', 'たちつてとっ'], ['な', 'なにぬねの'], ['は', 'はひふへほ'], ['ま', 'まみむめも'], ['や', 'やゆよゃゅょ'], ['ら', 'らりるれろ'], ['わ', 'わをん']];
  const kanaRow = (k) => { const c = toHira(k).normalize('NFD')[0]; const r = KROWS.find((x) => x[1].includes(c)); return r ? r[0] : '#'; };
  function azEntries() {
    const ja = state.lang === 'ja';
    const list = Object.values(S).map((st) => ({ st, label: tx(st.name), key: ja ? toHira(st.k || '') : tx(st.name) }));
    list.sort((a, b) => ja ? a.key.localeCompare(b.key, 'ja') : a.key.localeCompare(b.key, 'en', { sensitivity: 'base' }));
    const groups = [];
    list.forEach((e) => {
      const g = ja ? kanaRow(e.key) : ((e.label.match(/[A-Za-z]/) || ['#'])[0].toUpperCase());
      let last = groups[groups.length - 1];
      if (!last || last.g !== g) { last = { g, items: [] }; groups.push(last); }
      last.items.push(e);
    });
    return groups;
  }
  function renderAZ() {
    const groups = azEntries();
    const n = Object.keys(S).length;
    $('[data-az-count]').textContent = state.lang === 'ja'
      ? '六つの路線、' + n + 'の駅。地図と同じデータから作っています。選ぶと地図上の駅が開きます。'
      : n + ' stations on six lines, built from the same data as the map. Choose one to open it on the map.';
    $('[data-az-jump]').innerHTML = groups.map((g) => '<a href="#rbm-az-' + encodeURIComponent(g.g) + '" data-azjump="' + esc(g.g) + '">' + esc(g.g) + '</a>').join('');
    $('[data-az-list]').innerHTML = groups.map((g) => '<section class="rbm-azgroup" id="rbm-az-' + encodeURIComponent(g.g) + '" aria-labelledby="rbm-azh-' + encodeURIComponent(g.g) + '"><h3 class="rbm-azletter" id="rbm-azh-' + encodeURIComponent(g.g) + '">' + esc(g.g) + '</h3><ul>' +
      g.items.map(({ st, label }) => {
        const ln = LINES[st.line];
        const also = st.also ? ' <span aria-hidden="true">+</span> ' + badge(st.also) + '<span>' + esc(tx(LINES[st.also].short)) + '</span>' : '';
        return '<li><button type="button" data-az="' + st.id + '" title="' + esc(hoverText(st)) + '" aria-label="' + esc(label + ' — ' + u('line') + ' ' + st.line + ' · ' + tx(ln.name) + (st.also ? ' + ' + st.also + ' · ' + tx(LINES[st.also].name) : '')) + '">' +
          '<span class="rbm-azname">' + esc(label) + (st.flag ? '<span class="rbm-azflag" aria-hidden="true">*</span>' : '') + '</span>' +
          '<span class="rbm-azline">' + badge(st.line) + '<span>' + esc(tx(ln.short)) + '</span>' + also + '</span></button>' +
          (st.flag ? legalHTML() : '') + '</li>';
      }).join('') + '</ul></section>').join('');
  }
  /* View visibility. Desktop unchanged: map page shows every section, az page the index.
     Mobile (<900px): three tab views — MAP (entry, legend, zoomable map, panels, shelf), LINE (one line, vertical), INDEX (the A–Z). */
  function applyView() {
    const m = isMobile();
    const v = state.view;
    const mv = state.mview;
    $$('[data-mapview]').forEach((el) => { el.hidden = v !== 'map' || (m && mv !== 'map'); });
    $('[data-azview]').hidden = v !== 'az';
    const lv = $('[data-lineview]');
    if (lv) lv.hidden = !(m && v === 'map' && mv === 'line');
    $$('[data-view]').forEach((b) => {
      const dv = b.dataset.view;
      const pressed = dv === 'az' ? v === 'az'
        : dv === 'line' ? m && v === 'map' && mv === 'line'
        : v === 'map' && (!m || mv === 'map');
      b.setAttribute('aria-pressed', String(!!pressed));
    });
  }
  function setView(v, opts = {}) {
    state.view = v;
    applyView();
    // The React routes own the two desktop views; no hash rewrite here.
    if (opts.scroll !== false) scrollToEl(v === 'az' ? $('[data-azview]') : $('#rbm-start'));
  }

  /* LINE view (mobile only): one line at a time, top to bottom, from the same data + map geometry */
  function lineSeq(lid) {
    if (lid === 'A') return A_SEQ.slice();
    if (lid === 'F') return F_SEQ.slice();
    return Object.keys(POS).filter((id) => POS[id].y === Y[lid] && S[id] && (S[id].line === lid || S[id].also === lid)).sort((a, b) => POS[a].x - POS[b].x);
  }
  function renderLineView() {
    if (!isMobile()) return;
    const lid = state.mline;
    const ln = LINES[lid];
    $('[data-lvpicker]').innerHTML = ORDER.map((o) =>
      '<button type="button" class="rbm-lvpick" style="--lc:' + LINES[o].color + '" data-lvline="' + o + '" aria-pressed="' + (o === lid) + '">' + badge(o) + '<span>' + esc(tx(LINES[o].name)) + '</span></button>').join('');
    $('[data-lvlist]').innerHTML = '<ol class="rbm-lvline" style="--lc:' + ln.color + '">' + lineSeq(lid).map((id) => {
      const st = S[id];
      const open = state.lvSel === id;
      const other = st.also ? (st.also === lid ? st.line : st.also) : null;
      return '<li class="rbm-lvrow' + (other ? ' is-ix' : '') + (open ? ' is-open' : '') + '" data-lvrow="' + id + '">' +
        '<button type="button" class="rbm-lvst" data-lvst="' + id + '" aria-expanded="' + open + '">' +
          '<span class="rbm-lvname">' + esc(tx(st.name)) + (st.flag ? '<span class="rbm-azflag" aria-hidden="true">*</span>' : '') + '</span>' +
          '<span class="rbm-lvchev" aria-hidden="true">›</span></button>' +
        (other ? '<button type="button" class="rbm-lvchange" data-changefor="' + id + '" data-toline="' + other + '">' + esc(u('changeFor')) + ' ' + badge(other) + ' ' + esc(tx(LINES[other].name)) + '</button>' : '') +
        '<div class="rbm-lvdetail"' + (open ? '' : ' hidden') + '>' + (open ? stationBody(st, 'h3') : '') + '</div></li>';
    }).join('') + '</ol>';
  }
  function setMLine(lid, scroll) {
    if (!LINES[lid]) return;
    state.mline = lid;
    state.mview = 'line';
    state.view = 'map';
    renderLineView();
    applyView();
    if (scroll) scrollToEl($('[data-lineview]'), 8);
  }
  function showLineAt(id, opts = {}) {
    const st = S[id]; if (!st) return;
    state.sel = id;
    state.lvSel = id;
    state.view = 'map';
    state.mview = 'line';
    state.mline = opts.line || st.line;
    renderLineView();
    applyView();
    updateMap(false);
    renderPanels();
    if (opts.scroll !== false) {
      const row = $('[data-lvrow="' + id + '"]');
      scrollToEl(row || $('[data-lineview]'), 76);
    }
  }

  function renderAll() {
    renderStatic(); renderNav(); renderEntry(); renderMap(); renderPanels(); renderCards(); renderShelf(); renderAZ(); renderLineView();
    const dlg = $('[data-dialog]');
    if (dlg.open && dlg.dataset.line) fillDialog(dlg.dataset.line);
  }

  // Actions
  function select(id, opts = {}) {
    const st = S[id]; if (!st) return;
    state.sel = id;
    if (isMobile()) {
      state.cardSel[opts.card || st.line] = id;
      updateCards();
      updateMap(false);
      renderPanels();
      if (opts.toLine) { showLineAt(id); return; }
      if (opts.scroll !== false) scrollToEl($('[data-detail]'));
      return;
    }
    if (opts.card) {
      state.cardSel[opts.card] = id;
      updateCards();
    }
    updateMap(false);
    renderPanels();
    if (opts.scrollMap) scrollToEl($('#rbm-start'));
  }
  function setEntry(k) {
    state.entry = state.entry === k ? null : k;
    $$('[data-entry-k]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.entryK === state.entry)));
    updateMap(true);
    updateCards();
    renderPanels();
  }
  function fillDialog(lid) {
    const dlg = $('[data-dialog]');
    dlg.dataset.line = lid;
    $('[data-dlg-title]').textContent = `${u('line')} ${lid} — ${LINES[lid].n}`;
    $('[data-dlg-body]').innerHTML = cardHTML(lid, 'dlg');
    updateCards();
  }
  let dlgReturn = null;
  function openLine(lid) {
    if (isMobile()) { setMLine(lid, true); return; }
    const dlg = $('[data-dialog]');
    dlgReturn = document.activeElement;
    fillDialog(lid);
    if (!dlg.open) dlg.showModal();
    const first = dlg.querySelector('.rbm-chip'); if (first) first.focus();
  }
  function closeDialog() { const dlg = $('[data-dialog]'); if (dlg.open) dlg.close(); }
  const onDialogClose = () => { if (active && dlgReturn && document.contains(dlgReturn)) dlgReturn.focus(); dlgReturn = null; };
  const onDialogClick = (e) => { if (e.target === e.currentTarget) closeDialog(); };
  $('[data-dialog]').addEventListener('close', onDialogClose);
  $('[data-dialog]').addEventListener('click', onDialogClick);

  function closeNav(except) {
    $$('[data-nav]').forEach((b) => { if (b !== except) { b.setAttribute('aria-expanded', 'false'); const p = $(`#rbm-np-${b.dataset.nav}`); if (p) p.hidden = true; } });
  }

  /* Mobile map pan/zoom: fitted to width by default; drag pans, pinch zooms, +/− buttons zoom.
     Every entry point is gated on isMobile() — desktop never enters here. */
  const mapwrapEl = $('.rbm-mapwrap');
  const mapsvgEl = $('[data-map]');
  const zm = { k0: 1, k: 1, tx: 0, ty: 0, ptrs: new Map(), pinch: null, acc: 0, moved: false };
  const zmClamp = () => {
    zm.k = Math.min(Math.max(zm.k, zm.k0), zm.k0 * 6);
    const vw = mapwrapEl.clientWidth, vh = mapwrapEl.clientHeight;
    const mw = 1510 * zm.k, mh = 910 * zm.k;
    zm.tx = mw <= vw ? (vw - mw) / 2 : Math.min(0, Math.max(vw - mw, zm.tx));
    zm.ty = mh <= vh ? (vh - mh) / 2 : Math.min(0, Math.max(vh - mh, zm.ty));
  };
  const zmApply = () => { mapsvgEl.style.transform = 'translate(' + zm.tx.toFixed(1) + 'px,' + zm.ty.toFixed(1) + 'px) scale(' + zm.k.toFixed(4) + ')'; };
  function fitMobileMap() {
    if (!isMobile()) return;
    const vw = mapwrapEl.clientWidth;
    if (vw < 2) return;
    zm.k0 = vw / 1510; zm.k = zm.k0;
    zm.tx = 0; zm.ty = Math.max(0, (mapwrapEl.clientHeight - 910 * zm.k) / 2);
    zmApply();
  }
  function zoomAt(f, cx, cy) {
    const k2 = Math.min(Math.max(zm.k * f, zm.k0), zm.k0 * 6);
    const r = k2 / zm.k;
    zm.tx = cx - (cx - zm.tx) * r; zm.ty = cy - (cy - zm.ty) * r; zm.k = k2;
    zmClamp(); zmApply();
  }
  const zmRel = (e) => { const r = mapwrapEl.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  const onZmDown = (e) => {
    if (!isMobile() || (e.target.closest && e.target.closest('.rbm-zoom'))) return;
    zm.ptrs.set(e.pointerId, zmRel(e)); zm.acc = 0; zm.moved = false;
    if (zm.ptrs.size === 2) {
      const p = [...zm.ptrs.values()];
      zm.pinch = { d: Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y), k: zm.k, mx: (p[0].x + p[1].x) / 2, my: (p[0].y + p[1].y) / 2 };
      zm.moved = true;
    }
  };
  const onZmMove = (e) => {
    if (!isMobile() || !zm.ptrs.has(e.pointerId)) return;
    const prev = zm.ptrs.get(e.pointerId), cur = zmRel(e);
    zm.ptrs.set(e.pointerId, cur);
    zm.acc += Math.abs(cur.x - prev.x) + Math.abs(cur.y - prev.y);
    if (zm.acc > 8) zm.moved = true;
    if (zm.ptrs.size === 1) {
      if (!zm.moved) return;
      zm.tx += cur.x - prev.x; zm.ty += cur.y - prev.y;
      zmClamp(); zmApply();
    } else if (zm.ptrs.size === 2 && zm.pinch && zm.pinch.d > 0) {
      const p = [...zm.ptrs.values()];
      const d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
      const mx = (p[0].x + p[1].x) / 2, my = (p[0].y + p[1].y) / 2;
      const k2 = Math.min(Math.max(zm.pinch.k * (d / zm.pinch.d), zm.k0), zm.k0 * 6);
      zm.tx += mx - zm.pinch.mx; zm.ty += my - zm.pinch.my;
      const r = k2 / zm.k;
      zm.tx = mx - (mx - zm.tx) * r; zm.ty = my - (my - zm.ty) * r; zm.k = k2;
      zm.pinch.mx = mx; zm.pinch.my = my;
      zmClamp(); zmApply();
    }
  };
  const onZmUp = (e) => { zm.ptrs.delete(e.pointerId); if (zm.ptrs.size < 2) zm.pinch = null; };
  mapwrapEl.addEventListener('pointerdown', onZmDown);
  mapwrapEl.addEventListener('pointermove', onZmMove);
  mapwrapEl.addEventListener('pointerup', onZmUp);
  mapwrapEl.addEventListener('pointercancel', onZmUp);
  let rszTimer = null;
  const onWinResize = () => {
    if (!isMobile()) return;
    if (rszTimer) clearTimeout(rszTimer);
    /* refit only while the visitor hasn't zoomed, so a rotation doesn't steal their place */
    rszTimer = setTimeout(() => { if (active && Math.abs(zm.k - zm.k0) < 0.0001) fitMobileMap(); }, 160);
  };
  window.addEventListener('resize', onWinResize);

  const onRootClick = (e) => {
    if (zm.moved) { zm.moved = false; return; }
    const t = e.target.closest('button, a, [data-st], [data-line-open], [data-mline]');
    if (!t || !root.contains(t)) { closeNav(); return; }
    if (t.matches('[data-lang-toggle]')) { state.lang = state.lang === 'en' ? 'ja' : 'en'; renderAll(); return; }
    if (t.matches('[data-nav]')) {
      if (isMobile()) { openLine(t.dataset.nav); return; }
      const open = t.getAttribute('aria-expanded') === 'true';
      closeNav(t);
      t.setAttribute('aria-expanded', String(!open));
      $(`#rbm-np-${t.dataset.nav}`).hidden = open;
      return;
    }
    if (t.matches('[data-start]')) {
      e.preventDefault(); closeNav();
      if (state.view === 'az') setView('map', { scroll: false });
      scrollToEl($('#rbm-start'));
      const c = $('[data-entry-k]'); if (c) { const timer = setTimeout(() => { if (active) c.focus({ preventScroll: true }); }, reduceMotion ? 0 : 450); timers.push(timer); }
      return;
    }
    if (t.matches('[data-view]')) {
      closeNav();
      const dv = t.dataset.view;
      if (!isMobile()) { onNavigate(dv === 'az' ? '/services/index' : '/services/map'); return; }
      if (dv === 'az') { setView('az'); return; }
      if (dv === 'line') { setMLine(state.mline, true); return; }
      state.mview = 'map';
      setView('map');
      fitMobileMap();
      return;
    }
    if (t.matches('[data-azjump]')) { e.preventDefault(); scrollToEl($('#rbm-az-' + encodeURIComponent(t.dataset.azjump)), 60); return; }
    if (t.matches('[data-az]')) {
      if (isMobile()) { showLineAt(t.dataset.az); return; }
      onNavigate('/services/map?station=' + encodeURIComponent(t.dataset.az));
      return;
    }
    if (t.matches('[data-entry-k]')) { setEntry(t.dataset.entryK); return; }
    if (t.matches('[data-clear]')) { setEntry(state.entry); return; }
    if (t.matches('[data-close]')) { closeDialog(); return; }
    if (t.matches('[data-st]')) { select(t.dataset.st); return; }
    if (t.matches('[data-line-open]')) { closeNav(); if (state.view === 'az') setView('map', { scroll: false }); openLine(t.dataset.lineOpen); return; }
    if (t.matches('[data-viewmap]')) { closeNav(); scrollToEl($('#rbm-start')); return; }
    if (t.matches('[data-zoom]')) {
      if (isMobile()) {
        const zr = mapwrapEl.getBoundingClientRect();
        zoomAt(t.dataset.zoom === 'in' ? 1.35 : 1 / 1.35, zr.width / 2, zr.height / 2);
      }
      return;
    }
    if (t.matches('[data-mline]')) { closeNav(); if (isMobile()) setMLine(t.dataset.mline, true); return; }
    if (t.matches('[data-lvline]')) { setMLine(t.dataset.lvline, false); return; }
    if (t.matches('[data-lvst]')) {
      state.lvSel = state.lvSel === t.dataset.lvst ? null : t.dataset.lvst;
      if (state.lvSel) { state.sel = state.lvSel; renderPanels(); updateMap(false); }
      renderLineView();
      return;
    }
    if (t.matches('[data-changefor]')) { showLineAt(t.dataset.changefor, { line: t.dataset.toline }); return; }
    if (t.matches('[data-chip]')) {
      const card = t.closest('[data-card]');
      if (card && card.dataset.ctx === 'dlg') { closeDialog(); select(t.dataset.chip); return; }
      select(t.dataset.chip, { card: card ? card.dataset.card : null, scroll: false });
      return;
    }
    if (t.matches('[data-go]')) {
      const inNav = !!t.closest('.rbm-navpanel');
      const inDlg = !!t.closest('[data-dialog]');
      const inCard = t.closest('[data-card]');
      closeNav();
      if (inDlg) closeDialog();
      if (isMobile()) { select(t.dataset.go, { toLine: true }); return; }
      if (state.view === 'az') setView('map', { scroll: false });
      if (inCard && inCard.dataset.ctx === 'list') { select(t.dataset.go, { card: inCard.dataset.card, scroll: false }); return; }
      select(t.dataset.go, { scrollMap: inNav || t.closest('[data-shelf]') });
      return;
    }
  };
  root.addEventListener('click', onRootClick);
  const onRootKeydown = (e) => {
    if (e.key === 'Escape') { closeNav(); return; }
    const g = e.target.closest && e.target.closest('[data-st], g[data-line-open]');
    if (g && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); g.dataset.st ? select(g.dataset.st) : openLine(g.dataset.lineOpen); }
  };
  root.addEventListener('keydown', onRootKeydown);
  const onDocumentClick = (e) => { if (!e.target.closest('.rbm-navitem')) closeNav(); };
  document.addEventListener('click', onDocumentClick);
  const onMobileChange = () => {
    closeNav(); closeDialog(); renderPanels();
    if (isMobile()) { renderLineView(); fitMobileMap(); }
    else if (mapsvgEl) mapsvgEl.style.transform = '';
    applyView();
  };
  mobileQ.addEventListener('change', onMobileChange);

  // Marker: loops the Agent line; follows the route when one is drawn. Timer-driven, hidden under reduced motion.
  const marker = { t0: Date.now() };
  if (!reduceMotion) {
    const ease = (x) => 0.5 - Math.cos(Math.PI * x) / 2;
    markerInterval = setInterval(() => {
      if (document.hidden || isMobile()) return;
      const svg = $('[data-map]'); const m = svg && svg.querySelector('[data-marker]');
      if (!m) return;
      const rp = svg.querySelector('.rbm-routepath');
      const path = rp || svg.querySelector('[data-apath]');
      const len = path.getTotalLength();
      const travel = rp ? Math.max(4000, len / 0.09) : 16000;
      const hold = rp ? 1400 : 900;
      const el = Date.now() - marker.t0;
      if (el < 0) { m.setAttribute('opacity', '0'); return; }
      const ph = el % (travel + hold);
      const k = ease(Math.min(1, ph / travel));
      const pt = path.getPointAtLength(k * len);
      const fade = Math.min(1, ph / 400, Math.max(0, (travel + hold - ph) / 400));
      m.setAttribute('cx', pt.x.toFixed(1)); m.setAttribute('cy', pt.y.toFixed(1));
      m.setAttribute('opacity', fade.toFixed(2));
    }, 40);
  }

  // Deep link: /services#C (or #line-c) opens that line on arrival
  function openFromHash() {
    if (location.hash === '#az') { onNavigate('/services/index'); return; }
    const m = (location.hash || '').match(/^#(?:line-)?([catpsf])$/i);
    if (!m) return;
    if (state.view === 'az') { onNavigate('/services/map' + location.hash); return; }
    const lid = m[1].toUpperCase();
    if (isMobile()) { const timer = setTimeout(() => { if (active) setMLine(lid, true); }, 60); timers.push(timer); }
    else openLine(lid);
  }
  window.addEventListener('hashchange', openFromHash);

  renderAll();
  setView(state.view, { hash: false, scroll: false });
  if (isMobile()) fitMobileMap();
  openFromHash();
  if (initialStation && S[initialStation]) {
    if (isMobile()) showLineAt(initialStation);
    else select(initialStation, { scrollMap: false });
  }

  return {
    setLanguage(lang) {
      if (!active) return;
      state.lang = lang === 'jp' ? 'ja' : 'en';
      renderAll();
    },
    destroy() {
      active = false;
      timers.forEach((timer) => clearTimeout(timer));
      if (rszTimer) clearTimeout(rszTimer);
      if (markerInterval) clearInterval(markerInterval);
      mapwrapEl.removeEventListener('pointerdown', onZmDown);
      mapwrapEl.removeEventListener('pointermove', onZmMove);
      mapwrapEl.removeEventListener('pointerup', onZmUp);
      mapwrapEl.removeEventListener('pointercancel', onZmUp);
      window.removeEventListener('resize', onWinResize);
      root.removeEventListener('click', onRootClick);
      root.removeEventListener('keydown', onRootKeydown);
      document.removeEventListener('click', onDocumentClick);
      mobileQ.removeEventListener('change', onMobileChange);
      window.removeEventListener('hashchange', openFromHash);
      const dlg = $('[data-dialog]');
      if (dlg) {
        dlg.removeEventListener('close', onDialogClose);
        dlg.removeEventListener('click', onDialogClick);
        if (dlg.open) dlg.close();
      }
      document.documentElement.lang = previousDocumentLang;
    }
  };
}
